/**
 * Utility functions for Data Upload
 * Contains date detection, delimiter detection, and data sorting helpers
 */

// =============================================================================
// DATE FORMAT DETECTION
// =============================================================================

/**
 * Checks if a string is in a valid date format (without time)
 * Supports: YYYY-MM-DD, DD.MM.YYYY, DD/MM/YYYY, DD-MM-YYYY, YYYY/MM/DD, DD/MM/YY
 */
export const isDateFormat = (str: string): boolean => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$|^\d{2}\.\d{2}\.\d{4}$|^\d{2}\/\d{2}\/\d{4}$|^\d{2}-\d{2}-\d{4}$|^\d{4}\/\d{2}\/\d{2}$|^\d{2}\/\d{2}\/\d{2}$/;
  return dateRegex.test(str.trim());
};

/**
 * Checks if a string is in a valid time format (without date)
 * Supports: HH:MM, HH:MM:SS, HH:MM AM/PM, HH.MM.SS, HH-MM-SS
 */
export const isTimeFormat = (str: string): boolean => {
  const timeRegex = /^(?:\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AaPpMm]{2})?|\d{2}[:.-]\d{2}(?:[:.-]\d{2})?)$/;
  return timeRegex.test(str.trim());
};

/**
 * Checks if a string is in a valid datetime format (date and time combined).
 * Supports:
 *   - Space or `T` separator between date and time
 *   - Optional fractional seconds (1–6 digits, covers ms and μs)
 *   - Optional timezone offset (`+HHMM`, `+HH:MM`, `-HHMM`, `Z`)
 *   - Date parts: `YYYY-MM-DD`, `DD.MM.YYYY`, `DD/MM/YYYY`, `DD-MM-YYYY`, `YYYY/MM/DD`, `YYYYMMDD`
 *   - Time parts: 24-hour HH:MM[:SS[.ffffff]] or 12-hour HH:MM[:SS] AM/PM
 */
export const isDateTimeFormat = (str: string): boolean => {
  const datePart = '(\\d{4}-\\d{2}-\\d{2}|\\d{2}\\.\\d{2}\\.\\d{4}|\\d{2}\\/\\d{2}\\/\\d{4}|\\d{2}-\\d{2}-\\d{4}|\\d{4}\\/\\d{2}\\/\\d{2}|\\d{8})';
  const separator = '[\\sT]';
  const timePart = '(\\d{2}:\\d{2}(?::\\d{2}(?:\\.\\d{1,6})?)?|\\d{2}\\.\\d{2}(?:\\.\\d{2}(?:\\.\\d{1,6})?)?|\\d{2}-\\d{2}(?:-\\d{2}(?:\\.\\d{1,6})?)?|(?:0?[1-9]|1[0-2]):\\d{2}(?::\\d{2})?\\s?(?:AM|PM))';
  const timezone = '(?:Z|[+\\-]\\d{2}:?\\d{2})?';
  const fullRegex = new RegExp(`^${datePart}${separator}${timePart}${timezone}$`, 'i');
  return fullRegex.test(str.trim());
};

/**
 * Checks if datetime string has dummy/placeholder time (00:00 or 00:00:00).
 * Accepts both space and `T` as date/time separator.
 */
export const hasDummyTime = (str: string): boolean => {
  const dummyTimeRegex = /[\sT]00:00(?::00)?$/;
  return dummyTimeRegex.test(str.trim());
};

// =============================================================================
// DELIMITER DETECTION
// =============================================================================

/**
 * Detects the most likely delimiter in CSV content
 * Analyzes first 5 lines and returns the delimiter with highest occurrence
 */
export const detectDelimiter = (content: string): string => {
  const lines = content.split(/\r?\n/).slice(0, 5); // Check first 5 lines
  const delimiters = [';', ',', '\t', '|'];
  const counts: Record<string, number> = {};

  for (const delimiter of delimiters) {
    counts[delimiter] = 0;
    for (const line of lines) {
      if (line.trim()) {
        counts[delimiter] += (line.match(new RegExp(`\\${delimiter}`, 'g')) || []).length;
      }
    }
  }

  // Find delimiter with highest count
  let maxCount = 0;
  let detectedDelimiter = ';';
  for (const [delim, count] of Object.entries(counts)) {
    if (count > maxCount) {
      maxCount = count;
      detectedDelimiter = delim;
    }
  }

  return detectedDelimiter;
};

// =============================================================================
// DATA SORTING
// =============================================================================

/**
 * Parses various date string formats to ISO format
 * Supports: DD.MM.YYYY, MM/DD/YYYY, YYYY-MM-DD
 */
const parseDate = (dateStr: string): string => {
  dateStr = dateStr.trim();

  if (dateStr.includes('.')) {
    const [day, month, year] = dateStr.split('.');
    return `${year.trim()}-${month.trim().padStart(2, '0')}-${day.trim().padStart(2, '0')}`;
  } else if (dateStr.includes('/')) {
    const [month, day, year] = dateStr.split('/');
    return `${year.trim()}-${month.trim().padStart(2, '0')}-${day.trim().padStart(2, '0')}`;
  }
  return dateStr;
};

/**
 * Sorts 2D array data by date in first column
 * Assumes first row is header
 * Backend ALWAYS sends header as first row (e.g., ['UTC', '1'])
 */
export const sortByDate = (data: string[][]): string[][] => {
  if (!data || data.length <= 1) {
    return data;
  }

  // Backend ALWAYS sends header as first row
  const headers = data[0];
  const dataToSort = data.slice(1);

  // If there's no data to sort (only header), return as-is
  if (dataToSort.length === 0) {
    return data;
  }

  dataToSort.sort((a, b) => {
    try {
      const dateStrA = a[0].trim();
      const dateStrB = b[0].trim();

      let dateA = new Date(dateStrA);
      let dateB = new Date(dateStrB);

      if (isNaN(dateA.getTime())) {
        dateA = new Date(parseDate(dateStrA));
      }
      if (isNaN(dateB.getTime())) {
        dateB = new Date(parseDate(dateStrB));
      }

      const timeA = dateA.getTime();
      const timeB = dateB.getTime();

      if (!isNaN(timeA) && !isNaN(timeB)) {
        return timeA - timeB;
      }

      if (isNaN(timeA)) return 1;
      if (isNaN(timeB)) return -1;
      return 0;
    } catch {
      return 0;
    }
  });

  // Always return with header at the top
  return [headers, ...dataToSort];
};

// =============================================================================
// FILE READING HELPERS
// =============================================================================

/**
 * Reads file content as text with a size limit
 * @param file - File to read
 * @param maxBytes - Maximum bytes to read (default: 10KB)
 */
export const readFileContent = (file: File, maxBytes = 1024 * 10): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const slice = file.slice(0, maxBytes);

    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = () => {
      // Technical error message - caller adds translated context
      reject(new Error(reader.error?.message || 'FileReader error'));
    };
    reader.readAsText(slice);
  });
};
